import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const tierLabels = ['PERSONAL', 'PROFESSIONAL S', 'PROFESSIONAL M', 'ENTERPRISE'];
const prices = [
  { monthly: 49, yearly: 39 },
  { monthly: 99, yearly: 79 },
  { monthly: 179, yearly: 143 },
  { monthly: null, yearly: null },
];
const buttonLinks = [
  '/register?plan=personal',
  '/register?plan=professional-s',
  '/register?plan=professional-m',
  'mailto:info@coachdesk365.com?subject=Enterprise-Anfrage',
];
const defaultFeatured = 1;

const PricingSection: React.FC = () => {
  const { t } = useTranslation();
  const headerRef = useScrollAnimation<HTMLDivElement>();
  const gridRef = useScrollAnimation<HTMLDivElement>(0.05);
  const [yearly, setYearly] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultFeatured);

  const tiers = t('pricing.tiers', { returnObjects: true }) as {
    name: string; description: string; buttonLabel: string; features: string[]; priceLabel?: string;
  }[];

  return (
    <section id="pricing" className="hp-pricing hp-dot-pattern" aria-labelledby="pricing-title">
      <div className="hp-pricing-inner">
        <div ref={headerRef} className="hp-section-header hp-animate">
          <span className="hp-section-label">{t('pricing.sectionLabel')}</span>
          <h2 id="pricing-title">{t('pricing.title')}</h2>
          <p>{t('pricing.subtitle')}</p>

          <div className="hp-pricing-toggle">
            <span className={!yearly ? 'hp-toggle-active' : ''}>{t('pricing.monthly')}</span>
            <button
              className={`hp-toggle-switch${yearly ? ' hp-toggle-switch--active' : ''}`}
              onClick={() => setYearly(!yearly)}
              aria-label={t('pricing.toggleAriaLabel')}
            >
              <span className="hp-toggle-knob" />
            </button>
            <span className={yearly ? 'hp-toggle-active' : ''}>
              {t('pricing.yearly')} <span className="hp-pricing-save">{t('pricing.save')}</span>
            </span>
          </div>
        </div>

        <div ref={gridRef} className="hp-pricing-grid hp-animate hp-animate-stagger">
          {tiers.map((tier, i) => {
            const isFeatured = i === selectedIndex;
            const priceData = prices[i];
            const priceLabel = tier.priceLabel;
            const price = priceLabel
              ? priceLabel
              : `€${yearly ? priceData.yearly : priceData.monthly}`;
            const hasSuffix = priceData.monthly !== null;

            return (
              <div
                key={tierLabels[i]}
                className={`hp-pricing-card hp-animate-child${isFeatured ? ' hp-pricing-card--featured' : ''}`}
                style={{ transitionDelay: `${i * 0.1}s`, cursor: 'pointer' }}
                onClick={() => setSelectedIndex(i)}
              >
                {isFeatured && (
                  <span className="hp-pricing-featured-badge">{t('pricing.featuredBadge')}</span>
                )}

                <span className="hp-pricing-tier-label">{tierLabels[i]}</span>
                <h3 className="hp-pricing-name">{tier.name}</h3>
                <p className="hp-pricing-desc">{tier.description}</p>

                <div className="hp-pricing-price-row">
                  <span className={`hp-pricing-price${!hasSuffix ? ' hp-pricing-price--small' : ''}`}>
                    {price}
                  </span>
                  {hasSuffix && (
                    <span className="hp-pricing-suffix">{t('pricing.perMonth')}</span>
                  )}
                </div>

                {buttonLinks[i].startsWith('mailto') ? (
                  <a
                    href={buttonLinks[i]}
                    className={`hp-pricing-btn${isFeatured ? ' hp-pricing-btn--primary' : ''}`}
                  >
                    {tier.buttonLabel}
                  </a>
                ) : (
                  <Link
                    to={buttonLinks[i]}
                    className={`hp-pricing-btn${isFeatured ? ' hp-pricing-btn--primary' : ''}`}
                  >
                    {tier.buttonLabel}
                  </Link>
                )}

                <ul className="hp-pricing-features">
                  {tier.features.map((f, j) => (
                    <li key={j}>
                      <span className="hp-pricing-check" aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="hp-pricing-more">
                  <a href="#pricing" onClick={(e) => { e.preventDefault(); document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' }); }}>
                    {t('pricing.allFeatures')}
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
